import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  accountQuery,
  bonusIdsForSelection,
  composeBirthDate,
  login,
  register,
  resendSms,
  verifySms,
} from '@/api';
import { safeRedirect } from '@/lib/safeRedirect';
import {
  RegisterModal,
  RegisterThankYouModal,
  VerifySmsModal,
  type FastRegisterFormValues,
  type RegisterFormValues,
} from '@/ui/auth';

export const Route = createFileRoute('/register/')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = safeRedirect(search.redirect);
    return redirect ? { redirect } : {};
  },
  component: RegisterPage,
});

interface PendingCredentials {
  username: string;
  password: string;
}

interface PendingSms extends PendingCredentials {
  guid: string;
  phone: string;
}

type RegisterStep = 'form' | 'sms' | 'thankyou';

function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const { data: account } = useQuery(accountQuery());
  const [step, setStep] = useState<RegisterStep>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<PendingCredentials | null>(null);
  const [sms, setSms] = useState<PendingSms | null>(null);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const allowRegistration = account?.AllowUserRegistration !== false;
  const chatEnabled = account?.Chat === true;

  useEffect(() => {
    if (account && account.AllowUserRegistration === false) {
      void navigate({ href: '/' });
    }
  }, [account, navigate]);

  const leave = () => {
    const target = redirect ?? '/';
    void navigate({ href: target });
  };

  const showThankYou = (next: PendingCredentials) => {
    setCredentials(next);
    setSms(null);
    setStep('thankyou');
    setLoginError(null);
  };

  const finishLogin = async (username: string, password: string) => {
    setLoading(true);
    setLoginError(null);
    try {
      const result = await login(username, password, { persist: true });
      if (result.requiresSmsVerification) {
        const guid = result.user.Guid;
        const phone = result.user.Telephone;
        if (guid && phone) {
          setSms({ guid, phone, username, password });
          setStep('sms');
          setSmsError(
            t('SMS verification is required before you can sign in.', {
              defaultValue: 'SMS verification is required before you can sign in.',
            }),
          );
          return;
        }
        setLoginError(
          t('SMS verification is required before you can sign in.', {
            defaultValue: 'SMS verification is required before you can sign in.',
          }),
        );
        return;
      }
      await navigate({ href: redirect ?? '/' });
    } catch (err) {
      const message =
        err instanceof Error && err.message ? err.message : 'Wrong username or password';
      setLoginError(t(message, { defaultValue: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterResponse = (
    response: Awaited<ReturnType<typeof register>>,
    nextCredentials: PendingCredentials,
    phoneForSms: string,
  ) => {
    if (!response.Success) {
      const key = response.Error || 'Error Registering User';
      setError(t(key, { defaultValue: key }));
      return;
    }

    if (response.Verify && response.Guid) {
      setCredentials(nextCredentials);
      setSms({
        guid: response.Guid,
        phone: phoneForSms,
        ...nextCredentials,
      });
      setSmsError(null);
      setStep('sms');
      return;
    }

    // SMS send failure: still registered — show thank-you (legacy continues to success).
    if (response.Error === 'SMS was not sent.') {
      setLoginError(t('SMS was not sent.', { defaultValue: 'SMS was not sent.' }));
    }

    showThankYou(nextCredentials);
  };

  const handleSubmit = async (values: RegisterFormValues) => {
    setLoading(true);
    setError(null);

    try {
      const birthDate = composeBirthDate(values.birthDay, values.birthMonth, values.birthYear);
      if (!birthDate) {
        setError(t('Enter a valid date of birth', { defaultValue: 'Enter a valid date of birth' }));
        return;
      }

      const bonusIds = bonusIdsForSelection(
        values.bonusId ? { Id: values.bonusId, Type: values.bonusType } : null,
      );

      const response = await register({
        Username: values.username,
        Password: values.password,
        Email: values.email,
        Telephone: values.telephone,
        FirstName: values.firstName,
        LastName: values.lastName,
        Country: Number(values.countryId),
        CurrencyId: Number(values.currencyId),
        BirthDate: birthDate,
        ...(values.address ? { Address: values.address } : {}),
        ...(values.zipCode ? { ZipCode: values.zipCode } : {}),
        ...(values.city ? { City: values.city } : {}),
        ...(values.citizenId ? { CitizenId: values.citizenId } : {}),
        ...(values.promoCode ? { PromoCode: values.promoCode } : {}),
        ...bonusIds,
      });

      handleRegisterResponse(
        response,
        { username: values.username, password: values.password },
        values.telephone,
      );
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Error Registering User';
      setError(t(message, { defaultValue: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleFastSubmit = async (values: FastRegisterFormValues) => {
    setLoading(true);
    setError(null);

    try {
      const isPhone = values.mode === 'phone';
      const username = isPhone ? values.telephone : values.username;
      const response = await register({
        Username: username,
        Password: values.password,
        Country: Number(values.countryId),
        CurrencyId: Number(values.currencyId),
        ...(isPhone ? { Telephone: values.telephone } : {}),
      });

      handleRegisterResponse(
        response,
        { username, password: values.password },
        isPhone ? values.telephone : '',
      );
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Error Registering User';
      setError(t(message, { defaultValue: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code: string) => {
    if (!sms) return;
    setSmsLoading(true);
    setSmsError(null);
    try {
      const result = await verifySms({
        Guid: sms.guid,
        Phone: sms.phone,
        Code: code,
      });
      if (!result.Success) {
        const key = result.Error || 'VerificationCodeNotValid';
        setSmsError(t(key, { defaultValue: key }));
        return;
      }
      showThankYou({ username: sms.username, password: sms.password });
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'VerificationCodeNotValid';
      setSmsError(t(message, { defaultValue: message }));
    } finally {
      setSmsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!sms) return;
    const ok = await resendSms({ MachineGuid: sms.guid, Phone: sms.phone });
    if (!ok) {
      setSmsError(t('ResendFailed', { defaultValue: 'Failed to resend SMS' }));
    }
  };

  if (!allowRegistration) {
    return null;
  }

  return (
    <>
      <RegisterModal
        open={step === 'form'}
        dismissible
        onClose={leave}
        loading={loading}
        error={error}
        account={account}
        chatEnabled={chatEnabled}
        onSubmit={handleSubmit}
        onFastSubmit={handleFastSubmit}
      />
      <VerifySmsModal
        open={step === 'sms'}
        phone={sms?.phone ?? ''}
        loading={smsLoading}
        error={smsError}
        dismissible
        onClose={leave}
        onVerify={handleVerify}
        onResend={handleResend}
      />
      <RegisterThankYouModal
        open={step === 'thankyou'}
        dismissible
        onClose={leave}
        loading={loading}
        error={loginError}
        chatEnabled={chatEnabled}
        onLogin={() => {
          if (!credentials) return;
          void finishLogin(credentials.username, credentials.password);
        }}
      />
    </>
  );
}
